import { supabase } from "../lib/supabase";
import type { SearchParams, SortParams } from "../components/Manager/types";

export type StoreSet = {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
  store_count: number;
  stores_store_sets?: { store_id: number }[];
};

export type FetchResult<T> = {
  data: T[];
  hasMore: boolean;
  totalCount?: number;
};

export async function fetchStoreSets(
  limit?: number,
  offset?: number,
  searchParams?: SearchParams,
  sortParams?: SortParams,
): Promise<FetchResult<StoreSet>> {
  // Helper to apply all filters to a query
  const applyFilters = (q: any): any => {
    let result = q;

    // Apply text search filter
    if (searchParams?.text) {
      const searchText = `%${searchParams.text}%`;
      result = result.ilike("name", searchText);
    }

    return result;
  };

  // First, get total count with search filters
  let countQuery = supabase
    .from("store_sets")
    .select("*", { count: "exact", head: true });
  countQuery = applyFilters(countQuery);
  const { count } = await countQuery;

  // Now get the data with store count
  let query = supabase.from("store_sets").select(`
    *,
    stores_store_sets(store_id)
  `);

  query = applyFilters(query);

  // Apply sorting
  const sortColumn = sortParams?.column ?? "name";
  const sortAscending = sortParams?.order !== "desc";

  // For store_count sorting, we need to sort client-side after fetching
  if (sortColumn !== "store_count") {
    query = query.order(sortColumn, { ascending: sortAscending });
  }

  if (limit) {
    query = query.limit(limit + 1);
  }

  if (offset) {
    query = query.range(offset, offset + (limit ?? 10));
  }

  const { error, data } = await query;

  if (error) throw error;

  // Transform data to include store_count
  const items = (data ?? []).map((item) => ({
    ...item,
    store_count: item.stores_store_sets?.length ?? 0,
  })) as StoreSet[];

  // Sort by store_count if needed (client-side)
  if (sortColumn === "store_count") {
    items.sort((a, b) => {
      const diff = a.store_count - b.store_count;
      return sortAscending ? diff : -diff;
    });
  }

  const hasMore = limit ? items.length > limit : false;
  const limitedData = limit ? items.slice(0, limit) : items;

  return { data: limitedData, hasMore, totalCount: count ?? undefined };
}

/**
 * Search store sets by name
 */
export async function searchStoreSets(
  searchText: string,
  limit: number = 10,
): Promise<StoreSet[]> {
  const { data, error } = await supabase
    .from("store_sets")
    .select(
      `
      *,
      stores_store_sets(store_id)
    `,
    )
    .ilike("name", `%${searchText}%`)
    .limit(limit);

  if (error) throw error;

  return (data ?? []).map((item) => ({
    ...item,
    store_count: item.stores_store_sets?.length ?? 0,
  })) as StoreSet[];
}

/**
 * Fetch a single store set by ID
 */
export async function fetchStoreSetById(id: number): Promise<StoreSet | null> {
  const { data, error } = await supabase
    .from("store_sets")
    .select(
      `
      *,
      stores_store_sets(store_id)
    `,
    )
    .eq("id", id)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null;
    throw error;
  }

  return {
    ...data,
    store_count: data.stores_store_sets?.length ?? 0,
  } as StoreSet;
}

/**
 * Fetch multiple store sets by IDs
 */
export async function fetchStoreSetsByIds(ids: number[]): Promise<StoreSet[]> {
  if (ids.length === 0) return [];

  const { data, error } = await supabase
    .from("store_sets")
    .select(
      `
      *,
      stores_store_sets(store_id)
    `,
    )
    .in("id", ids);

  if (error) throw error;

  return (data ?? []).map((item) => ({
    ...item,
    store_count: item.stores_store_sets?.length ?? 0,
  })) as StoreSet[];
}

/**
 * Check if a store set has marketing activities attached
 */
export async function hasStoreSetMarketingActivities(
  storeSetId: number,
): Promise<boolean> {
  const { count, error } = await supabase
    .from("marketing_activities_store_sets")
    .select("*", { count: "exact", head: true })
    .eq("store_set_id", storeSetId);

  if (error) throw error;

  return (count ?? 0) > 0;
}

/**
 * Get store IDs for a store set
 */
export async function getStoreSetStoreIds(
  storeSetId: number,
): Promise<number[]> {
  const { data, error } = await supabase
    .from("stores_store_sets")
    .select("store_id")
    .eq("store_set_id", storeSetId);

  if (error) throw error;

  return (data ?? []).map((item) => item.store_id);
}

/**
 * Update store set stores (replace all)
 */
export async function updateStoreSetStores(
  storeSetId: number,
  storeIds: number[],
): Promise<void> {
  // Delete all existing links
  const { error: deleteError } = await supabase
    .from("stores_store_sets")
    .delete()
    .eq("store_set_id", storeSetId);

  if (deleteError) throw deleteError;

  // Insert new links
  if (storeIds.length > 0) {
    const inserts = storeIds.map((storeId) => ({
      store_id: storeId,
      store_set_id: storeSetId,
    }));

    const { error: insertError } = await supabase
      .from("stores_store_sets")
      .insert(inserts);

    if (insertError) throw insertError;
  }
}
